import type { Locale } from '@/i18n/types';
import { api } from '@/lib/api/client';
import type { JournalResponse } from '@/lib/api/types';
import type { FlightDetail, FlightHistoryRow, FlightRow, ScheduleCriteria } from '@/types/flight';

/**
 * The online table.
 *
 * A POST, not a GET: the filter, the page and the sort travel as one body.
 */
export const fetchSchedule = (
  criteria: ScheduleCriteria,
  signal?: AbortSignal,
): Promise<JournalResponse<FlightRow>> =>
  api.post<JournalResponse<FlightRow>>('/online-table/filter', prune(criteria), signal);

/**
 * Drops what should not be sent.
 *
 * A blank string is not a filter — the server trims and ignores it, but sending
 * `""` in a body that also carries a page number invites a future server that
 * treats it as "match empty". More importantly `rows: 0` is removed: the row query
 * returns nothing for it while the count query still reports the real total, so the
 * table would show no rows beside a count of 1 234 and a page count computed from a
 * page size nobody asked for. It looks exactly like a server fault and is not one.
 */
const prune = (criteria: ScheduleCriteria): ScheduleCriteria => {
  const body: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(criteria)) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    // An empty list is no filter, the same as a blank string.
    if (Array.isArray(value) && value.length === 0) continue;
    if ((key === 'rows' || key === 'pageNum') && Number(value) < 1) continue;

    body[key] = typeof value === 'string' ? value.trim() : value;
  }

  return body as ScheduleCriteria;
};

/**
 * The two exports. Same filter object as `/filter`, but they export EVERY matching
 * row: the server ignores `pageNum` and `rows` entirely, so both are dropped here
 * rather than sent and ignored — the request then states what it means. The caller
 * keeps the period filter on: without a `scheduledDate` range this asks for the
 * whole table.
 *
 * They are not two renderings of one report. The spreadsheet is ten columns for a
 * person to read; APCAS is a sixty-four-column fixed-position interchange file for
 * another system. Pick by who reads it.
 */
const exportBody = (criteria: ScheduleCriteria): ScheduleCriteria => {
  const filter = { ...prune(criteria) };
  delete filter.pageNum;
  delete filter.rows;
  return filter;
};

/**
 * `locale` picks the header row AND the translated flight type and status — the
 * server accepts `kk` as Kazakh, so the app's own locale name is sent as it is.
 */
export const downloadScheduleExcel = (criteria: ScheduleCriteria, locale: Locale): Promise<Blob> =>
  api.download('/online-table/download/excel', { ...exportBody(criteria), locale });

export const downloadScheduleApcas = (criteria: ScheduleCriteria): Promise<Blob> =>
  api.download('/online-table/download/apcas', exportBody(criteria));

/**
 * One flight in full.
 *
 * **A missing id is `200 null`, not a 404.** So the caller cannot treat "the request
 * worked" as "the flight exists" — hence the explicit null in the return type.
 */
export const fetchFlight = (id: number, signal?: AbortSignal): Promise<FlightDetail | null> =>
  api.get<FlightDetail | null>(`/online-table/id/${id}`, undefined, signal);

/**
 * Every change to a flight, oldest first.
 *
 * These are the integration events for the row, merged with a frozen archive of
 * changes older than the changeover — one shape either way, with the archived rows
 * distinguishable by their coarse `AIRPORT` source and their missing old values.
 */
export const fetchFlightHistory = (id: number, signal?: AbortSignal): Promise<FlightHistoryRow[]> =>
  api
    .get<FlightHistoryRow[]>(`/online-table/history/scheduleId/${id}`, undefined, signal)
    .then((rows) => (Array.isArray(rows) ? rows : []));
